import styled from "@emotion/styled";
import { theme, useResponsive } from "@product/scmp-sdk";
import { useAtomValue } from "jotai";
import type { FunctionComponent } from "react";
import { useEffect, useState } from "react";

import { googleImaAtom } from "~/components/advertisement/google-ima/atoms";
import { useIntersection } from "~/lib/hooks";

import { VideoPlayMode } from "./enums";
import type { Props as SCMPYouTubeVideoPlayerProps } from "./scmp-youtube-video-player";
import { SCMPYouTubeVideoPlayer } from "./scmp-youtube-video-player";
import { SCMPYoutubeVideoPreview } from "./scmp-youtube-video-preview";
import { SCMPYoutubeVideoContainer, StyledSCMPYouTubeVideoFooter } from "./styles";

export type Props = {
  className?: string;
  duration?: string;
  enableAutoPlay: boolean;
  imageSource?: string;
  index: number;
  initialVideoPlayMode?: VideoPlayMode;
  isEnabledImaManager?: boolean;
  isHeroVideo: boolean;
  isMuteVideo?: boolean;
  mobileImageSource?: string;
  withFooter?: boolean;
  youtubeVideoId?: string;
} & Omit<SCMPYouTubeVideoPlayerProps, "videoId" | "videoPlayMode" | "queryReference">;

const Component: FunctionComponent<Props> = ({
  className,
  duration,
  imageSource,
  index = 0,
  initialVideoPlayMode = VideoPlayMode.Unset,
  isEnabledImaManager = true,
  isHeroVideo,
  isMuteVideo = true,
  title,
  withFooter = true,
  youtubeVideoId,
  ...attribs
}: Props) => {
  const [videoPlayMode, setVideoPlayMode] = useState(initialVideoPlayMode);
  const { isImaReady } = useAtomValue(googleImaAtom);
  const renderPreviewOrVideo = () => {
    if (youtubeVideoId && videoPlayMode !== VideoPlayMode.Unset && isImaReady) {
      return (
        <SCMPYouTubeVideoPlayer
          index={index}
          isEnabledImaManager={isEnabledImaManager}
          isMuteVideo={isMuteVideo}
          videoId={youtubeVideoId}
          videoPlayMode={videoPlayMode}
          {...{ ...attribs, title }}
        />
      );
    } else if (title && duration && imageSource) {
      return (
        <SCMPYoutubeVideoPreview
          duration={duration}
          imageSource={imageSource}
          isHeroVideo={isHeroVideo}
          title={title}
          onClick={() => {
            setVideoPlayMode(VideoPlayMode.Manual);
          }}
        />
      );
    }
    return null;
  };

  const { captureIntersectionElement, intersection } = useIntersection({
    threshold: 0.45,
  });
  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"));

  useEffect(() => {
    if (intersection?.isIntersecting && isDesktopUp) {
      setVideoPlayMode(VideoPlayMode.Autoplay);
    }
  }, [intersection?.isIntersecting, isDesktopUp]);

  return (
    <SCMPYoutubeVideoContainer className={className} ref={captureIntersectionElement}>
      {renderPreviewOrVideo()}
      {withFooter && <StyledSCMPYouTubeVideoFooter title={title} />}
    </SCMPYoutubeVideoContainer>
  );
};

Component.displayName = "SCMPYoutubeVideo";
export const SCMPYoutubeVideo = styled(Component)``;
